import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, Link as LinkIcon, List, ListOrdered, Send } from 'lucide-react';
import { createDiaryEntry } from '../../../api';
import DiaryCreations from './DiaryCreations';
import DiaryRecorder from './DiaryRecorder';

const RAINBOW_COLORS = ['text-red-500', 'text-orange-500', 'text-amber-500', 'text-green-500', 'text-teal-500', 'text-blue-500', 'text-purple-500'];

function RainbowWord({ word }) {
  return (
    <>
      {word.split('').map((letter, index) => (
        <span key={index} className={RAINBOW_COLORS[index % RAINBOW_COLORS.length]}>
          {letter}
        </span>
      ))}
    </>
  );
}

const TABS = [
  { id: 'video', label: 'Video', icon: '🎬', bg: 'bg-violet-100', text: 'text-violet-700', active: 'bg-violet-600' },
  { id: 'audio', label: 'Audio', icon: '🎵', bg: 'bg-pink-100', text: 'text-pink-600', active: 'bg-pink-500' },
  { id: 'photo', label: 'Photo', icon: '🖼️', bg: 'bg-teal-100', text: 'text-teal-700', active: 'bg-teal-500' },
  { id: 'text', label: 'Text / Article', icon: '📄', bg: 'bg-orange-100', text: 'text-orange-700', active: 'bg-orange-500' },
];

const TAB_COPY = {
  video: { title: 'Record or Upload a Video', subtitle: "Show us what you've been working on!", accept: 'video/*' },
  audio: { title: 'Record or Upload Audio', subtitle: 'Share a voice note, song, or sound story!', accept: 'audio/*' },
  photo: { title: 'Upload a Photo', subtitle: 'Share a picture of your art, work, or a happy moment!', accept: 'image/*' },
};

// These mirror the backend's rules (backend/constants/diary.js) so a too-big file
// or too-long title is caught here rather than after a long upload. Keep in sync.
const TITLE_MAX_LENGTH = 200;
const NOTE_MAX_LENGTH = 1000;
const MEDIA_MAX_MB = { photo: 10, audio: 30, video: 100 };
const MISSING_FILE_MESSAGE = {
  video: 'Record a video first!',
  audio: 'Record some audio first!',
  photo: 'Choose a photo file first!',
};
const TITLE_SUBJECT = { text: 'article', video: 'video', audio: 'audio', photo: 'photo' };

// The chosen File lives in DiaryZone (`file` / `onFileChange`) - it's what gets saved -
// and the panel just shows it, so the picker can never disagree with what will be uploaded.
function MediaTabPanel({ tabId, file, onFileChange }) {
  const [fileUrl, setFileUrl] = useState(null);
  const copy = TAB_COPY[tabId];
  const fileName = file ? file.name : null;

  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFileChange = (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    onFileChange(chosen);
  };

  const inputId = `diary-file-${tabId}`;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl font-extrabold text-slate-900">{copy.title}</h3>
        <p className="text-sm font-medium text-gray-500">{copy.subtitle}</p>
      </div>

      <label
        htmlFor={inputId}
        className="rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 py-10 px-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <span className="text-4xl">{tabId === 'video' ? '🎬' : tabId === 'audio' ? '🎙️' : '🖼️'}</span>
        <span className="text-[15px] font-bold text-slate-700">
          {fileName ? fileName : `Tap to choose a ${tabId} file`}
        </span>
        <input id={inputId} type="file" accept={copy.accept} className="hidden" onChange={handleFileChange} />
      </label>

      {fileUrl && tabId === 'video' && <video src={fileUrl} controls className="w-full rounded-xl" />}
      {fileUrl && tabId === 'audio' && <audio src={fileUrl} controls className="w-full" />}
      {fileUrl && tabId === 'photo' && <img src={fileUrl} alt="Selected upload preview" className="w-full rounded-xl" />}
    </div>
  );
}

// Formatting isn't wired up yet: every toolbar button shows the same "coming soon" toast.
const TOOLBAR_ACTIONS = [
  { id: 'bold', label: 'Bold', content: <span className="font-serif text-xl font-black">B</span> },
  { id: 'italic', label: 'Italic', content: <span className="font-serif text-xl font-bold italic">I</span> },
  { id: 'underline', label: 'Underline', content: <span className="font-serif text-xl font-bold underline">U</span> },
  { id: 'numbered-list', label: 'Numbered list', content: <ListOrdered size={20} strokeWidth={2.25} /> },
  { id: 'bulleted-list', label: 'Bulleted list', content: <List size={20} strokeWidth={2.25} /> },
  { id: 'link', label: 'Insert link', content: <LinkIcon size={20} strokeWidth={2.25} /> },
];

function TextTabPanel({ text, onChangeText }) {
  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <span className="h-[52px] w-[52px] rounded-2xl bg-orange-500 text-white flex items-center justify-center shrink-0">
          <FileText size={28} strokeWidth={2.25} />
        </span>
        <div>
          <h3 className="text-2xl font-extrabold text-slate-900">Write a Text / Article</h3>
          <p className="text-sm font-medium text-slate-600">Write your thoughts, stories, ideas or anything you want to share!</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border-b border-gray-200 bg-white">
          {/* The icon group spreads out to ~360px on wide cards; when the card is too narrow to fit it beside the counter, the counter drops to its own line instead of being clipped. */}
          <div className="flex flex-1 min-w-[200px] max-w-[360px] items-center justify-between">
            {TOOLBAR_ACTIONS.map(({ id, label, content }) => (
              <button
                key={id}
                type="button"
                aria-label={label}
                title={label}
                onClick={() => toast('Formatting coming soon!', { icon: '✍️' })}
                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-700 hover:bg-gray-100 hover:text-slate-900 transition-colors"
              >
                {content}
              </button>
            ))}
          </div>
          <span className="ml-auto shrink-0 pr-1 text-sm font-semibold text-slate-600 whitespace-nowrap">
            {text.length} / 1000 characters
          </span>
        </div>
        <textarea
          value={text}
          onChange={(event) => onChangeText(event.target.value.slice(0, 1000))}
          placeholder="Start writing here..."
          rows={10}
          className="flex-1 w-full px-4 py-4 text-[15px] text-slate-800 placeholder:text-gray-400 focus:outline-none resize-none"
        />
      </div>
    </div>
  );
}

export default function DiaryZone() {
  const [mode, setMode] = useState('create'); // 'create' | 'creations'
  const [activeTab, setActiveTab] = useState('text');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [mediaFile, setMediaFile] = useState(null); // what will be saved on a Video / Audio / Photo tab: a recording or a chosen photo
  const [pickerResetCount, setPickerResetCount] = useState(0); // bumped after publishing to clear the file picker
  const [isPublishing, setIsPublishing] = useState(false);
  const [isRecording, setIsRecording] = useState(false); // the Video / Audio recorder is mid-recording (or starting)

  const isRecorderTab = activeTab === 'video' || activeTab === 'audio';

  // Each tab starts with nothing chosen or recorded (its panel also remounts per tab).
  useEffect(() => {
    setMediaFile(null);
    setIsRecording(false);
  }, [activeTab]);

  const handlePublish = async () => {
    if (isPublishing || isRecording) return;

    if (activeTab === 'text' && !text.trim()) {
      toast.error('Write something before publishing!');
      return;
    }
    if (activeTab !== 'text') {
      if (!mediaFile) {
        toast.error(MISSING_FILE_MESSAGE[activeTab]);
        return;
      }
      if (mediaFile.size > MEDIA_MAX_MB[activeTab] * 1024 * 1024) {
        toast.error(`That ${activeTab} is too big! The limit is ${MEDIA_MAX_MB[activeTab]}MB.`);
        return;
      }
    }
    if (!title.trim()) {
      toast.error('Give it a title first!');
      return;
    }

    const formData = new FormData();
    formData.append('type', activeTab);
    formData.append('title', title.trim());
    if (note.trim()) formData.append('note', note.trim());
    if (activeTab === 'text') {
      formData.append('content', text.trim());
    } else {
      formData.append('file', mediaFile);
    }

    setIsPublishing(true);
    try {
      await createDiaryEntry(formData);
      toast.success('Published to your Diary!');
      setText('');
      setTitle('');
      setNote('');
      setMediaFile(null);
      setPickerResetCount((count) => count + 1);
    } catch (error) {
      // Keep everything they typed / picked so they can just try again.
      toast.error(error.response?.data?.message || 'Could not publish right now. Please try again!');
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="text-3xl font-extrabold text-slate-900 flex-1 min-w-0">
            My <RainbowWord word="Digital" /> Diary
          </h1>
          <button
            onClick={() => setMode('create')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-extrabold transition-colors ${
              mode === 'create' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            }`}
          >
            ✏️ Create New
          </button>
          <button
            onClick={() => setMode('creations')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-extrabold transition-colors ${
              mode === 'creations' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            }`}
          >
            📁 My Creations
          </button>
        </div>
      </div>

      {mode === 'creations' ? (
        <DiaryCreations tabs={TABS} />
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            {TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-2 px-6 py-3.5 rounded-2xl font-extrabold transition-colors ${
                    isActive ? `${tab.active} text-white shadow-md` : `${tab.bg} ${tab.text} hover:brightness-95`
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                  {isActive && (
                    <span
                      className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 ${tab.active}`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* One card: the editor / media picker on the left, the shared Title, note and
              Publish on the right, split by a divider. The split is a container query on
              the card's own width rather than the xl *viewport* breakpoint - the sidebar
              eats ~280px, so a ~1270px window only has a ~945px main area and would never
              reach xl. Below 800px the two halves stack. */}
          <div className="[container-type:inline-size]">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 grid grid-cols-1 gap-6 [@container_(min-width:800px)]:grid-cols-[minmax(0,1fr)_minmax(290px,36%)]">
              <div className="min-w-0 flex flex-col">
                {activeTab === 'text' && <TextTabPanel text={text} onChangeText={setText} />}
                {activeTab === 'photo' && (
                  <MediaTabPanel key={`photo-${pickerResetCount}`} tabId="photo" file={mediaFile} onFileChange={setMediaFile} />
                )}
                {isRecorderTab && (
                  <DiaryRecorder
                    key={`${activeTab}-${pickerResetCount}`}
                    kind={activeTab}
                    file={mediaFile}
                    onFileChange={setMediaFile}
                    onRecordingChange={setIsRecording}
                  />
                )}
              </div>

              <div className="min-w-0 flex flex-col gap-4 border-t border-gray-200 pt-6 [@container_(min-width:800px)]:border-t-0 [@container_(min-width:800px)]:border-l [@container_(min-width:800px)]:pl-6 [@container_(min-width:800px)]:pt-9">
                <div>
                  <label className="text-base font-extrabold text-slate-900">Title</label>
                  <input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder={`Give a title to your ${TITLE_SUBJECT[activeTab]}...`}
                    maxLength={TITLE_MAX_LENGTH}
                    className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="text-base font-extrabold text-slate-900">Add a note (optional)</label>
                  <textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder={
                      activeTab === 'text'
                        ? 'Tell us a little about it...\n(e.g. what inspired you to write this?)'
                        : 'Tell us a little about it...'
                    }
                    maxLength={NOTE_MAX_LENGTH}
                    rows={4}
                    className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] resize-y focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>

                <button
                  onClick={handlePublish}
                  disabled={isPublishing || isRecording}
                  title={isRecording ? 'Stop recording first' : undefined}
                  className="mt-auto flex items-center justify-center gap-3 rounded-2xl bg-orange-500 text-white text-xl font-extrabold py-4 hover:bg-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={24} fill="currentColor" /> {isPublishing ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
